import React, { useState, useEffect } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { compose } from "redux";
import { connect, useDispatch } from "react-redux";
import classNames from "classnames";
import { Image, Button } from "semantic-ui-react";
import { Lookup, SidePanel } from "@redriver/cinnamon";
import NotesSidePanel from "../Notes";
import { CalculationBreakdownSidePanel } from "features/Sheets/ManageSheet/SheetActions";
import {
  getSheetState,
  getSheetDataState,
  LookupNames,
  ActionTypes,
  ImpersonateUsersLookupDropdown,
} from "features/Sheets";
import {
  setSidePanelVisibility,
  setSheetViewType,
  setImpersonateUser,
  defaultImpersonatedUser,
  listSheetAudits,
} from "../actions";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import {
  UserTypeCheck,
  withCurrentUser,
  PermissionCheck,
} from "features/../../../shared/components/auth";
import {
  AuditLogEntity,
  SheetViewType,
  UserType,
} from "features/../../../shared/constants/enums";
import Scanner from "features/../../../shared/assets/scanner.svg";
import Building from "features/../../../shared/assets/building.svg";
import User from "features/../../../shared/assets/user.svg";
import RenameSheet from "./RenameSheet";
import { SheetViewConfirmationModal } from "../Shared";
import { invalidSystemScheduleItemsExist } from "features/Sheets/ManageSheet";
import { getDealerOfficeHeaderInfo } from "features/../../../shared/features/DealerOffices";
import { useTranslation } from "react-i18next";
import ListAudits from "components/audit/ListAudits";
import { AuditColumns } from "constants/enums";
import {
  ImpersonatedUserPermissionCheck,
  withImpersonatedPermissions,
  ImpersonatedUserUserTypeCheck,
  ImpersonatedUserUserAreaCheck,
} from "modules/ImpersonatedUserPermissions";
import { UserArea } from "mortar/Principal/Enums";

const SheetHeader = ({
  sheetId,
  sheetViewType,
  setSheetViewType,
  sheetDetailsSaving,
  calculating,
  invalidSchedule,
  customerToggle,
  customerView,
  impersonatedUser,
  setImpersonateUser,
  defaultImpersonatedUser,
  impersonatedUserFlicker,
  setImpersonatedUserFlicker,
  currentUser,
  location,
  preventUpdates,
}) => {
  const { t } = useTranslation("sheetHeader");
  const history = useHistory();
  const { isInternalUser } = currentUser;

  const [showOnSheetFirstLoad, setShowOnSheetFirstLoad] = useState(false);
  const disableShowOnSheetFirstLoad = () => setShowOnSheetFirstLoad(false);

  const [tz, setTz] = useState(undefined);

  const SheetViewTypeButton = ({ sheetViewTypeToCheck, label, className }) => (
    <Button
      className={classNames(
        "view-button",
        sheetViewType === sheetViewTypeToCheck && "active",
        className,
      )}
      onClick={() => {
        setSheetViewType(sheetViewTypeToCheck);
        setImpersonatedUserFlicker(false);
        defaultImpersonatedUser();
      }}
    >
      {label}
    </Button>
  );

  const dispatch = useDispatch();
  useEffect(() => {
    (async function getHeaderInfo() {
      let headerInfo = await dispatch(getDealerOfficeHeaderInfo({ sheetId }));
      setTz(headerInfo.success ? headerInfo.response.timezone : null);
    })();
  }, [sheetId, dispatch]);

  useEffect(() => {
    setShowOnSheetFirstLoad(location?.state?.isNewSheet);
    history.replace({ state: {} });
  }, [location?.state?.isNewSheet, history]);

  if (!sheetId) return null;

  return (
    <Lookup
      lookup={LookupNames[ActionTypes.HeaderDetails]}
      lookupParams={{ sheetId }}
      resetOnMount={true}
      render={({ loading, response }, resetLookup) =>
        loading || !response ? null : (
          <React.Fragment>
            <div className="sheet-header">
              <div className="info-actions">
                <div className="sheet-title">
                  <h5>
                    {response.name}
                    <RenameSheet
                      sheetId={sheetId}
                      lookupResponse={response}
                      resetLookup={resetLookup}
                      showOnSheetFirstLoad={showOnSheetFirstLoad}
                      disableShowOnSheetFirstLoad={disableShowOnSheetFirstLoad}
                      disabled={preventUpdates}
                    />
                  </h5>
                  <small>
                    {response.reference
                      ? t("ScheduleReference", { ref: response.reference })
                      : t("NoScheduleAssigned")}
                  </small>
                </div>
                <div className="sheet-icon-detail">
                  <Image src={Building} />
                  <p>{response.dealer}</p>
                </div>
                <div className="sheet-icon-detail">
                  <Image src={User} />
                  <p>{response.client}</p>
                </div>
                <div className="sheet-icon-detail">
                  <Image src={Scanner} />
                  <p>{t(`enums:calcType.${response.calcType}`)}</p>
                </div>
                <div className="history-and-action-buttons">
                  {!customerView && (
                    <div className="sheet-action-buttons">
                      <ImpersonatedUserUserTypeCheck restrictTo={UserType.HQ}>
                        <SidePanel
                          dimmable
                          closeIcon
                          className="audit-list"
                          header={t("Audit_Log")}
                          trigger={
                            <Button
                              primary
                              icon="search"
                              content={t("Audit")}
                            />
                          }
                        >
                          <ListAudits
                            entityId={sheetId}
                            auditLevel={AuditLogEntity.Sheet}
                            loadAudits={listSheetAudits}
                            columns={[
                              AuditColumns.User,
                              AuditColumns.DateTime,
                              AuditColumns.Audit,
                            ]}
                          />
                        </SidePanel>
                      </ImpersonatedUserUserTypeCheck>
                      <ImpersonatedUserPermissionCheck
                        action={Actions.View}
                        target={Targets.SheetNote}
                      >
                        <NotesSidePanel title={t("Notes")} sheetId={sheetId} />
                      </ImpersonatedUserPermissionCheck>
                      {isInternalUser &&
                        sheetViewType === SheetViewType.IpsAdmin && (
                          <CalculationBreakdownSidePanel />
                        )}
                    </div>
                  )}
                </div>
              </div>
              <div className="view-buttons-impersonate">
                <div className="view-buttons">
                  {!customerView && (
                    <React.Fragment>
                      <label>{t("View")}</label>
                      <Button.Group>
                        <ImpersonatedUserUserTypeCheck restrictTo={UserType.HQ}>
                          <SheetViewTypeButton
                            sheetViewTypeToCheck={SheetViewType.IpsAdmin}
                            label={t("IPS_Admin")}
                            className="ips-admin"
                          />
                        </ImpersonatedUserUserTypeCheck>

                        <ImpersonatedUserUserAreaCheck
                          restrictTo={[
                            UserArea.IpsSuperUser,
                            UserArea.IpsAdmin,
                            UserArea.SupplierAdmin,
                          ]}
                        >
                          <SheetViewTypeButton
                            sheetViewTypeToCheck={SheetViewType.SupplierAdmin}
                            label={t("Supplier_Admin")}
                            className="supplier-admin"
                          />
                        </ImpersonatedUserUserAreaCheck>

                        <SheetViewTypeButton
                          sheetViewTypeToCheck={SheetViewType.AccountManager}
                          label={t("Account_Manager")}
                          className="account-manager"
                        />
                        <SheetViewConfirmationModal
                          disabled={
                            sheetDetailsSaving ||
                            calculating ||
                            invalidSchedule ||
                            customerView
                          }
                          customerToggle={customerToggle}
                          customerView={customerView}
                          triggerButtonClassName={classNames(
                            "view-button",
                            customerView && "active",
                            "customer",
                          )}
                          triggerButtonText={t("Customer")}
                          hideIcon={true}
                          setImpersonatedUserFlicker={
                            setImpersonatedUserFlicker
                          }
                        />
                      </Button.Group>
                    </React.Fragment>
                  )}
                </div>
                <UserTypeCheck restrictTo={UserType.HQ}>
                  <PermissionCheck
                    target={Targets.SheetAdmin}
                    action={Actions.ImpersonateUser}
                  >
                    {!customerView && (
                      <div className="impersonate">
                        <div className="tag">{t("Impersonate")}:</div>
                        <div className="header-dropdown">
                          {impersonatedUserFlicker ? (
                            <ImpersonateUsersLookupDropdown
                              clearable
                              search
                              placeholder="User..."
                              sheetId={sheetId}
                              sheetViewType={sheetViewType}
                              onChange={async (event, data) => {
                                if (data.value === "") {
                                  defaultImpersonatedUser();
                                  setSheetViewType(
                                    currentUser.userType === UserType.HQ
                                      ? SheetViewType.IpsAdmin
                                      : currentUser.userArea ===
                                        UserArea.SupplierAdmin
                                      ? SheetViewType.SupplierAdmin
                                      : SheetViewType.AccountManager,
                                  );
                                } else {
                                  const newImpUser = await setImpersonateUser(
                                    data.value,
                                  );

                                  setSheetViewType(
                                    newImpUser.userType === UserType.HQ
                                      ? SheetViewType.IpsAdmin
                                      : newImpUser.userArea ===
                                        UserArea.SupplierAdmin
                                      ? SheetViewType.SupplierAdmin
                                      : SheetViewType.AccountManager,
                                  );
                                }
                              }}
                            />
                          ) : (
                            setImpersonatedUserFlicker(true)
                          )}
                        </div>
                      </div>
                    )}
                  </PermissionCheck>
                </UserTypeCheck>
              </div>
            </div>
          </React.Fragment>
        )
      }
    />
  );
};

const mapStateToProps = (state) => {
  const { sheetViewType, sheetDetailsSaving, calculating, sheetData } =
    getSheetState(state);
  const { impersonatedUser, preventUpdates } = getSheetDataState(state);

  return {
    sheetViewType,
    sheetDetailsSaving,
    calculating,
    invalidSchedule: invalidSystemScheduleItemsExist(sheetData),
    impersonatedUser,
    preventUpdates,
  };
};

const actions = {
  setSidePanelVisibility,
  setSheetViewType,
  setImpersonateUser,
  defaultImpersonatedUser,
};

export default compose(
  connect(mapStateToProps, actions),
  withCurrentUser,
  withImpersonatedPermissions,
  withRouter,
)(SheetHeader);
